import { useEffect, useMemo, useState } from "react";
import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import debounce from "lodash.debounce";
import isEqual from "lodash.isequal";
import {
    SEARCH_TABLE_DEFAULT_PAGE_SIZE,
    SEARCH_TABLE_LARGE_PAGE_DEBOUNCE_MS,
    SEARCH_TABLE_LARGE_PAGE_SIZE_THRESHOLD,
} from "src/components/pages/search/components/table/consts";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";

interface IUseSearchTablePagination {
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
}

export const useSearchTablePagination = (searchParams: ISearchRequestParamsOmitCountry | null): IUseSearchTablePagination => {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: SEARCH_TABLE_DEFAULT_PAGE_SIZE,
    });
    const [lastAppliedSearchParams, setLastAppliedSearchParams] = useState<ISearchRequestParamsOmitCountry | null>(searchParams);

    if (!isEqual(searchParams, lastAppliedSearchParams)) {
        setLastAppliedSearchParams(searchParams);
        setPagination(current => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
    }

    const debouncedSetPagination = useMemo(() => debounce(setPagination, SEARCH_TABLE_LARGE_PAGE_DEBOUNCE_MS), []);
    useEffect(() => () => debouncedSetPagination.cancel(), [debouncedSetPagination]);

    const onPaginationChange: OnChangeFn<PaginationState> = updater => {
        const nextPagination = typeof updater === "function" ? updater(pagination) : updater;

        if (nextPagination.pageSize > SEARCH_TABLE_LARGE_PAGE_SIZE_THRESHOLD) {
            debouncedSetPagination(nextPagination);
            return;
        }

        setPagination(nextPagination);
    };

    return { pagination, onPaginationChange };
};
